import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { NotificationService } from "@/gen/omnios/v1/notification_connect";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Bell, Mail, Smartphone } from "lucide-react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/settings/notifications")({
	component: NotificationSettingsPage,
});

const client = createClient(NotificationService, transport);

const CHANNEL_CONFIG = [
	{ id: "in_app", icon: Bell, labelKey: "notifications_channel_in_app" },
	{ id: "push", icon: Smartphone, labelKey: "notifications_channel_push" },
	{ id: "email", icon: Mail, labelKey: "notifications_channel_email" },
];

function NotificationSettingsPage() {
	const { t } = useTranslation("settings");
	useBreadcrumbs([
		{ label: t("breadcrumb_settings"), href: "/settings" },
		{ label: t("notifications_breadcrumb") },
	]);

	const qc = useQueryClient();

	const { data: eventTypesData, isLoading: eventsLoading } = useQuery({
		queryKey: ["notification-event-types"],
		queryFn: () => client.listEventTypes({}),
	});

	const { data: prefsData, isLoading: prefsLoading } = useQuery({
		queryKey: ["notification-preferences"],
		queryFn: () => client.getPreferences({}),
	});

	const updateMut = useMutation({
		mutationFn: (args: {
			eventType: string;
			channel: string;
			enabled: boolean;
		}) => client.updatePreference(args),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["notification-preferences"] });
		},
	});

	const isLoading = eventsLoading || prefsLoading;

	if (isLoading) {
		return (
			<div className="space-y-4">
				<Skeleton className="h-8 w-48" />
				<Skeleton className="h-64" />
			</div>
		);
	}

	const eventTypes = eventTypesData?.eventTypes ?? [];
	const prefs = prefsData?.preferences ?? [];
	const prefMap = new Map(
		prefs.map((p) => [`${p.eventType}:${p.channel}`, p.enabled]),
	);

	// Group events by category extracted from the display_name
	const grouped = eventTypes.reduce(
		(acc, et) => {
			const category = et.category || t("notifications_category_general");
			if (!acc[category]) acc[category] = [];
			acc[category].push({
				id: et.id,
				label: et.label,
				channels: new Set(et.channels),
			});
			return acc;
		},
		{} as Record<
			string,
			{ id: string; label: string; channels: Set<string> }[]
		>,
	);

	const categories = Object.keys(grouped);

	if (categories.length === 0) {
		return (
			<Card className="border-dashed">
				<CardContent className="flex flex-col items-center justify-center py-12 text-center">
					<Bell className="h-12 w-12 text-muted-foreground/50" />
					<h3 className="mt-4 text-lg font-semibold">
						{t("notifications_no_events")}
					</h3>
				</CardContent>
			</Card>
		);
	}

	return (
		<div className="space-y-6">
			<p className="text-sm text-muted-foreground">
				{t("notifications_preferences_desc")}
			</p>

			{categories.map((category) => (
				<div key={category} className="space-y-3">
					<h3 className="text-sm font-medium">{category}</h3>
					<div className="rounded-lg border overflow-hidden">
						<Table>
							<TableHeader>
								<TableRow className="bg-muted/30">
									<TableHead>{t("notifications_col_event")}</TableHead>
									{CHANNEL_CONFIG.map((ch) => (
										<TableHead key={ch.id} className="text-center w-24">
											<div className="flex items-center justify-center gap-1.5">
												<ch.icon className="h-4 w-4" />
												<span className="hidden sm:inline">
													{t(ch.labelKey)}
												</span>
											</div>
										</TableHead>
									))}
								</TableRow>
							</TableHeader>
							<TableBody>
								{grouped[category].map((evt) => (
									<TableRow key={evt.id} className="hover:bg-muted/50">
										<TableCell>{evt.label}</TableCell>
										{CHANNEL_CONFIG.map((ch) => {
											const available = evt.channels.has(ch.id);
											const key = `${evt.id}:${ch.id}`;
											const enabled = available
												? (prefMap.get(key) ?? true)
												: false;
											return (
												<TableCell key={ch.id} className="text-center">
													{available ? (
														<Checkbox
															checked={enabled}
															disabled={updateMut.isPending}
															onCheckedChange={(v) => {
																updateMut.mutate({
																	eventType: evt.id,
																	channel: ch.id,
																	enabled: v === true,
																});
															}}
														/>
													) : (
														<span className="text-muted-foreground/30">—</span>
													)}
												</TableCell>
											);
										})}
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</div>
			))}
		</div>
	);
}
